import { computed } from 'vue';
import { useStore } from 'vuex';
import { updateAccount } from '@gpx-spa/queries';
import {
  readAccountDefaultBoolean,
  readAccountDefaultNumber,
  type AccountDefaultCode,
} from '@/utils/account-defaults';

export { ACCOUNT_DEFAULT_CODE } from '@/utils/account-defaults';
export type { AccountDefaultCode } from '@/utils/account-defaults';

type SettingValue = number | boolean | null;

export function useAccountDefaults() {
  const store = useStore();
  const { mutateAsync, isPending } = updateAccount();

  const account = computed(() => store.getters['auth/account']);
  const settings = computed(() => store.getters['auth/accountSettings']);

  const readNumber = (code: AccountDefaultCode) =>
    readAccountDefaultNumber(settings.value, code);

  const readBoolean = (code: AccountDefaultCode) =>
    readAccountDefaultBoolean(settings.value, code);

  const save = (changed: Partial<Record<AccountDefaultCode, SettingValue>>) =>
    mutateAsync({
      accountId: account.value.id,
      payload: { settings: changed },
    }).then((updatedAccount: { settings: unknown }) => {
      // `getAccount` has no call sites here, so the mutation's cache invalidation is a
      // no-op — the Vuex blob every consumer reads has to be synced by hand.
      store.dispatch('auth/setAccountSettings', {
        accountId: account.value.id,
        settings: updatedAccount.settings,
      });
    });

  return { readNumber, readBoolean, save, isSaving: isPending };
}
